import { Fragment } from "react";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DOCUMENT_CITATION_CHIP_MAX_WIDTH_PX } from "@/constants";
import {
  documentCitationLabel,
  documentSourceMetaLine,
  isDocumentSource,
  parseCitedText,
  sourceHostname,
  sourceLabel,
  sourcesById,
  type CitationSource,
  type CitedDocumentSource,
} from "@/shared/lib/citations";

type CitedTextProps = {
  text: string;
  sources?: CitationSource[];
};

/** Shared by the linked and the unresolved marker so both keep the same footprint in a line of
 * prose — an unresolved one differs only in colour and in not being a link. */
const MARKER_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 15,
  height: 15,
  px: 0.4,
  ml: 0.25,
  borderRadius: 0.75,
  fontSize: "0.62rem",
  fontWeight: 700,
  lineHeight: 1,
  verticalAlign: "super",
  textDecoration: "none",
} as const;

function CitationTooltipContent({ source }: { source: CitationSource }) {
  const label = sourceLabel(source);
  if (isDocumentSource(source)) {
    const metaLine = documentSourceMetaLine(source);
    return (
      <Box sx={{ maxWidth: 260 }}>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
          {label}
        </Typography>
        {metaLine ? (
          <Typography variant="caption" sx={{ display: "block", opacity: 0.85 }}>
            {metaLine}
          </Typography>
        ) : null}
      </Box>
    );
  }

  const hostname = sourceHostname(source.url);
  const metaLine = [source.publisher, source.published_date].filter(Boolean).join(" · ");

  return (
    <Box sx={{ maxWidth: 260 }}>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
        {label}
      </Typography>
      {metaLine ? (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.85 }}>
          {metaLine}
        </Typography>
      ) : null}
      {label === hostname ? null : (
        <Typography
          variant="caption"
          sx={{ display: "block", opacity: 0.85, wordBreak: "break-all" }}
        >
          {hostname}
        </Typography>
      )}
    </Box>
  );
}

function CitationMarker({
  sourceId,
  source,
}: {
  sourceId: number;
  source?: CitationSource;
}) {
  if (!source) {
    // Rendered anyway, muted: a marker with no matching source is an authoring gap, and silently
    // deleting it would hide the fact that the claim was meant to be backed by something.
    return (
      <Box
        component="span"
        sx={{
          ...MARKER_SX,
          color: "var(--theme-text-secondary)",
          bgcolor: "var(--theme-surface-alt)",
        }}
      >
        {sourceId}
      </Box>
    );
  }

  if (isDocumentSource(source)) {
    return <DocumentCitation source={source} />;
  }

  return (
    <Tooltip title={<CitationTooltipContent source={source} />} placement="top">
      <Box
        component="a"
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Source ${sourceId}: ${sourceLabel(source)} (opens in a new tab)`}
        // Cards and list rows are themselves click targets; a citation opens its source and
        // nothing else.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        sx={{
          ...MARKER_SX,
          cursor: "pointer",
          color: "var(--theme-primary-light)",
          bgcolor: "color-mix(in srgb, var(--theme-primary-light) 18%, transparent)",
          "&:hover": {
            bgcolor: "color-mix(in srgb, var(--theme-primary-light) 34%, transparent)",
          },
        }}
      >
        {sourceId}
      </Box>
    </Tooltip>
  );
}

/** A citation of one of the domain's own documents, named inline instead of numbered.
 *
 * A web citation is a `[n]` superscript because its name is a URL nobody wants in a sentence; a
 * document's name is the file the reader already holds, so it is shown in place — "Q1 Review.pdf
 * · p. 7" — with a document icon marking it as a document citation rather than a web one.
 *
 * Not a link: there is nothing to open, and an `<a>` with no href reads as a broken link to a
 * screen reader. The full title, period and page are in the tooltip; a long file name is
 * ellipsized here and shown whole there and in the source list. */
function DocumentCitation({ source }: { source: CitedDocumentSource }) {
  const { t } = useTranslation("common");
  const label = documentCitationLabel(source);

  return (
    <Tooltip title={<CitationTooltipContent source={source} />} placement="top">
      <Box
        component="span"
        aria-label={t("citations.documentCitationAriaLabel", { label })}
        // Cards and list rows are themselves click targets; a citation is never one of them.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          maxWidth: DOCUMENT_CITATION_CHIP_MAX_WIDTH_PX,
          px: 0.6,
          py: 0.1,
          ml: 0.5,
          borderRadius: 0.75,
          fontSize: "0.7rem",
          fontWeight: 600,
          lineHeight: 1.4,
          verticalAlign: "baseline",
          color: "var(--theme-primary-light)",
          bgcolor: "color-mix(in srgb, var(--theme-primary-light) 14%, transparent)",
          cursor: "default",
        }}
      >
        <DescriptionOutlined sx={{ fontSize: "0.8rem", flexShrink: 0 }} aria-hidden />
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Authored text with its `[1]`-style markers rendered as linked superscripts — or, for a marker
 * that resolves to a document page, as that document's name (see `DocumentCitation`).
 *
 * Emits inline content only — no wrapper element — so the caller keeps its own `Typography`,
 * colour and `pre-wrap`. That is what lets every section render citations the same way:
 * a nugget's insights, why and business impact, and a chat turn's action research, all wrap
 * their existing text node in this and gain links wherever the author put a marker.
 */
export function CitedText({ text, sources }: CitedTextProps) {
  const segments = parseCitedText(text);
  const sourceIndex = sourcesById(sources);

  return (
    <>
      {segments.map((segment, segmentIndex) => {
        // A document named inline twice in a row — `[2][2]`, or two statements off the same
        // page — would print the same file name back to back. A repeated number is harmless as a
        // superscript; a repeated file name is noise.
        const previous = segments[segmentIndex - 1];
        if (
          segment.kind === "citation" &&
          previous?.kind === "citation" &&
          previous.sourceId === segment.sourceId
        ) {
          return null;
        }
        return segment.kind === "text" ? (
          <Fragment key={segmentIndex}>{segment.text}</Fragment>
        ) : (
          <CitationMarker
            key={segmentIndex}
            sourceId={segment.sourceId}
            source={sourceIndex.get(segment.sourceId)}
          />
        );
      })}
    </>
  );
}
